import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Play, Square, Clock, CircleDollarSign, CalendarDays, Wallet, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { employeeApi, money, hrs, timeOf, dayLabel, errMsg } from '../api';
import { EmployeeShell, goldCard, CircleIcon } from '../EmployeeShell';

const fmtElapsed = (secs) => {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = Math.floor(secs % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const GoldDivider = () => <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />;

export default function EmployeeHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const timer = useRef(null);

  const load = () => Promise.all([employeeApi.me(), employeeApi.summary()])
    .then(([a, b]) => { setMe(a.data); setSum(b.data); })
    .catch((e) => { if (e.response?.status === 401) { localStorage.removeItem('employee_token'); navigate('/workforce'); } });

  useEffect(() => { load(); }, []);
  useEffect(() => {
    timer.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer.current);
  }, []);

  if (!me || !sum) return <EmployeeShell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div></EmployeeShell>;

  const rate = sum.hourly_rate_cents;
  const active = sum.active_entry;
  const liveSecs = active ? Math.max(0, (now - new Date(active.clock_in_at).getTime()) / 1000) : 0;
  const liveCents = Math.round(liveSecs / 3600 * rate);
  const todayCents = sum.today.earnings_cents + liveCents;
  const todaySecs = sum.today.seconds + liveSecs;
  const weekCents = sum.week.earnings_cents + liveCents;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(errMsg(e, 'Something went wrong')); }
    finally { setBusy(false); }
  };

  return (
    <EmployeeShell>
      <h1 className="text-white font-brand font-black text-3xl tracking-tight">Hey {me.name.split(' ')[0]} 👋</h1>

      <div className={`mt-5 rounded-2xl border-2 p-6 ${active ? 'border-[#D4AF37]/70 bg-gradient-to-b from-[#191512] to-[#0C0B08] shadow-[0_0_45px_rgba(212,175,55,0.15)]' : 'border-gray-800 bg-gradient-to-b from-[#161616] to-[#0C0C0C]'}`} data-testid="clock-card">
        {active ? (
          <>
            <div className="flex items-center justify-center gap-2.5">
              <CircleIcon icon={Clock} />
              <span className="font-brand text-[11px] font-bold uppercase tracking-[0.22em] text-[#D4AF37]">Clocked In · Since {timeOf(active.clock_in_at)}</span>
            </div>
            <div className="font-brand font-black text-6xl text-center mt-4 tabular-nums text-transparent bg-clip-text bg-gradient-to-b from-[#F5D77A] via-[#D4AF37] to-[#A8842B]" data-testid="live-earnings">{money(liveCents)}</div>
            <div className="my-5"><GoldDivider /></div>
            <div className="grid grid-cols-2 divide-x divide-gray-800">
              <div className="flex items-center justify-center gap-3 px-2">
                <CircleIcon icon={Timer} />
                <div>
                  <div className="text-white font-brand font-extrabold text-lg tabular-nums leading-tight" data-testid="live-elapsed">{fmtElapsed(liveSecs)}</div>
                  <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">This Shift</div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3 px-2">
                <CircleIcon icon={CircleDollarSign} />
                <div>
                  <div className="text-white font-brand font-extrabold text-lg tabular-nums leading-tight">{money(rate)}<span className="text-gray-400 text-sm font-bold">/hr</span></div>
                  <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">Hourly Rate</div>
                </div>
              </div>
            </div>
            <button onClick={() => act(employeeApi.clockOut, 'Clocked out — nice work!')} disabled={busy}
              className="mt-6 w-full flex items-center justify-center gap-2.5 bg-gradient-to-b from-[#E5484D] to-[#A02026] hover:from-[#D03A40] hover:to-[#8C1A1F] text-white font-brand font-black uppercase tracking-[0.12em] py-5 text-lg rounded-2xl border border-red-400/40 shadow-[0_8px_30px_rgba(200,40,45,0.35)] transition-colors disabled:opacity-60" data-testid="clock-out-btn">
              {busy ? <Loader2 size={20} className="animate-spin" /> : <Square size={18} />} Clock Out
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2.5">
              <CircleIcon icon={Clock} />
              <span className="font-brand text-[11px] font-bold uppercase tracking-[0.22em] text-gray-500">Not Clocked In</span>
            </div>
            <div className="flex items-center justify-center gap-3 mt-5">
              <CircleIcon icon={CircleDollarSign} />
              <div>
                <div className="text-white font-brand font-extrabold text-lg tabular-nums leading-tight">{money(rate)}<span className="text-gray-400 text-sm font-bold">/hr</span></div>
                <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">Your Rate</div>
              </div>
            </div>
            <button onClick={() => act(employeeApi.clockIn, "You're on the clock!")} disabled={busy}
              className="mt-6 w-full flex items-center justify-center gap-2.5 bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] hover:from-[#D4AF37] hover:to-[#B8941F] text-black font-brand font-black uppercase tracking-[0.12em] py-5 text-lg rounded-2xl border border-[#F5D77A]/60 shadow-[0_8px_30px_rgba(212,175,55,0.3)] transition-colors disabled:opacity-60" data-testid="clock-in-btn">
              {busy ? <Loader2 size={20} className="animate-spin" /> : <Play size={18} />} Clock In
            </button>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className={`${goldCard} p-4`} data-testid="today-card">
          <CircleIcon icon={CalendarDays} size="lg" />
          <div className="font-brand text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] mt-3">Today</div>
          <div className="text-white font-brand font-black text-2xl mt-1 tabular-nums">{money(todayCents)}</div>
          <div className="text-gray-500 text-xs mt-0.5">{hrs(todaySecs)}</div>
        </div>
        <div className={`${goldCard} p-4`} data-testid="week-card">
          <CircleIcon icon={Wallet} size="lg" />
          <div className="font-brand text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] mt-3 leading-snug">Paycheck (This Week)</div>
          <div className="text-white font-brand font-black text-2xl mt-1 tabular-nums">{money(weekCents)}</div>
          <div className="text-gray-500 text-xs mt-0.5">{hrs(sum.week.seconds + liveSecs)} · Mon–Sun</div>
        </div>
      </div>

      {sum.week.days.length > 0 && (
        <div className="mt-6">
          <h2 className="font-brand text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37] mb-2.5">This Week</h2>
          <div className="space-y-2">
            {sum.week.days.map((d) => (
              <div key={d.date} className={`${goldCard} px-4 py-3 flex justify-between items-center`}>
                <div>
                  <div className="text-white text-sm font-semibold">{dayLabel(d.date)}</div>
                  <div className="text-gray-500 text-xs">{d.entries.map((e) => `${timeOf(e.clock_in_at)} – ${timeOf(e.clock_out_at)}`).join(', ')}</div>
                </div>
                <div className="text-right">
                  <div className="text-[#D4AF37] font-semibold text-sm">{money(d.total_earnings_cents)}</div>
                  <div className="text-gray-500 text-xs">{hrs(d.total_seconds)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </EmployeeShell>
  );
}
